import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PDFViewer, Page, Text, View, Document, StyleSheet, Font, Svg } from '@react-pdf/renderer';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import './styles.css';
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Button,
  Switch,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';

import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import DogReport from './DogReport';

import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';

const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER_URL;

// Stranica sa pregledom pretplatnika; ?vlasnik=ID filtrira tabelu i otvara kerove tog vlasnika
const VLASNIK_URL = '/dashboard/HuntersReportAll';

// ----------------------------------------------------------------------
// Kolone tabele
// search: kako radi pretraga kad je kolona izabrana (klikom na naslov)
//   'exact'   - tačna brojčana vrijednost (ID)
//   'text'    - sadrži tekst
//   'numeric' - broj ili izraz: 3, >0, >=5, <2, <=1
//   null      - bez pretrage
// sortable: false - kolona se ne može sortirati

const TABLE_HEAD = [
  { id: 'id', label: 'Broj', alignRight: false, search: 'exact' },
  { id: 'tip', label: 'Tip', alignRight: false, search: 'text' },
  { id: 'vlasnik', label: 'Vlasnik', alignRight: false, search: 'text' },
  { id: 'ime', label: 'Ime', alignRight: false, search: 'text' },
  { id: 'tiptrackera', label: 'Tip trackera', alignRight: false, search: 'text' },
  { id: 'IMEI', label: 'IMEI', alignRight: false, search: 'text' },
  { id: 'aktivan', label: 'Aktivan', alignRight: false, search: 'numeric' },
  { id: 'online', label: 'Online', alignRight: false, search: 'numeric' },
  { id: 'onlinetoday', label: 'Online danas', alignRight: false, search: 'numeric' },
  { id: 'datumlast', label: 'Datum last', alignRight: false, search: 'text' },
  { id: 'datumend', label: 'Datum end', alignRight: false, search: 'text' },
  { id: 'saldo', label: 'Saldo', alignRight: false, search: 'numeric' },
  { id: 'update', label: 'Update', alignRight: false, search: null, sortable: false },
  { id: 'share', label: 'Share', alignRight: false, search: null, sortable: false },
];

const getColumn = (id) => TABLE_HEAD.find((c) => c.id === id);

// ----------------------------------------------------------------------
// Sortiranje i pretraga

function compareValues(a, b) {
  // Brojevi numerički, tekst abecedno (uz ispravno Č, Ć, Š, Ž, Đ)
  const na = Number(a);
  const nb = Number(b);
  if (a !== '' && b !== '' && a !== null && b !== null && !Number.isNaN(na) && !Number.isNaN(nb)) {
    return na - nb;
  }
  return String(a ?? '').localeCompare(String(b ?? ''), 'bs', { sensitivity: 'base' });
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => compareValues(b[orderBy], a[orderBy])
    : (a, b) => compareValues(a[orderBy], b[orderBy]);
}

// Brojčana pretraga: '3', '=3', '>0', '>=5', '<2', '<=1'
function matchNumeric(value, query) {
  const q = query.replace(/\s+/g, '');
  if (/^(>=|<=|>|<|=)?$/.test(q)) return true; // tek započet izraz, npr. samo '>'

  const m = q.match(/^(>=|<=|>|<|=)?(-?\d+)$/);
  if (!m) return false;

  const op = m[1] || '=';
  const n = Number(m[2]);
  const v = Number(value);

  switch (op) {
    case '>':
      return v > n;
    case '<':
      return v < n;
    case '>=':
      return v >= n;
    case '<=':
      return v <= n;
    default:
      return v === n;
  }
}

function matchesSearch(row, field, query) {
  const column = getColumn(field);
  const mode = column ? column.search : null;
  const q = query.trim().toLowerCase();

  switch (mode) {
    case 'exact':
      return /^\d+$/.test(q) ? Number(row[field]) === Number(q) : false;
    case 'text':
      return String(row[field] ?? '').toLowerCase().includes(q);
    case 'numeric':
      return matchNumeric(row[field], q);
    default:
      return true;
  }
}

function applySortFilter(array, comparator, query, field) {
  const sorted = array
    .map((el, index) => [el, index])
    .sort((a, b) => comparator(a[0], b[0]) || a[1] - b[1])
    .map((el) => el[0]);

  if (!query || !query.trim()) return sorted;

  return sorted.filter((row) => matchesSearch(row, field, query));
}

function getSearchPlaceholder(field) {
  const column = getColumn(field);
  if (!column) return 'Traži...';

  switch (column.search) {
    case 'exact':
      return `Traži po: ${column.label} (tačan broj)`;
    case 'text':
      return `Traži po: ${column.label}`;
    case 'numeric':
      return `Traži po: ${column.label} (npr. 1, >0, <=2)`;
    default:
      return `Pretraga nije dostupna za: ${column.label}`;
  }
}

// ----------------------------------------------------------------------
// Stilizovani switch (definisan jednom, izvan komponente)

const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 8,
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    '&:before, &:after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 16,
      height: 16,
    },
    '&:before': {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 12,
    },
    '&:after': {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 12,
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: 'none',
    width: 16,
    height: 16,
    margin: 2,
  },
}));

// ----------------------------------------------------------------------
// PDF font i stilovi

Font.register({
  family: 'Roboto',
  src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
});

const REPORT_COLS = [
  { key: 'rb', label: 'R.br.', width: '5%' },
  { key: 'vlasnik', label: 'Vlasnik', width: '25%', left: true },
  { key: 'ime', label: 'Ime', width: '20%', left: true },
  { key: 'tiptrackera', label: 'Tip trackera', width: '15%', left: true },
  { key: 'IMEI', label: 'IMEI', width: '20%', left: true },
  { key: 'datumend', label: 'Datum end', width: '15%' },
];

const styles = StyleSheet.create({
  body: {
    paddingTop: 40,
    paddingBottom: 65,
    paddingHorizontal: 30,
    fontFamily: 'Roboto',
    fontSize: 10,
  },
  svgTitle: {
    marginVertical: 5,
    paddingHorizontal: 20,
  },
  textmediumlarge: {
    fontSize: 20,
    fontFamily: 'Roboto',
  },
  summary: {
    fontSize: 10,
    marginBottom: 12,
    paddingHorizontal: 20,
  },
  table: {
    display: 'table',
    width: 'auto',
  },
  tableRow: {
    margin: 0,
    flexDirection: 'row',
  },
  tableHeader: {
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
    backgroundColor: '#e8f4e8',
  },
  rowOdd: {
    backgroundColor: '#f5f5f5',
  },
  tableCellLeft: {
    marginTop: 4,
    marginBottom: 4,
    marginLeft: 3,
    fontSize: 9,
    textAlign: 'left',
  },
  tableCellCenter: {
    marginTop: 4,
    marginBottom: 4,
    fontSize: 9,
    textAlign: 'center',
  },
  officer: {
    paddingTop: 50,
    left: 250,
  },
  officertext: {
    margin: 0,
    fontSize: 12,
    textAlign: 'center',
    fontFamily: 'Roboto',
  },
  mp: {
    position: 'absolute',
    fontSize: 12,
    bottom: 60,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'grey',
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'grey',
  },
});

function DevicesReportAll({ rows, datum }) {
  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.body} wrap>
        <Text style={styles.summary}>{datum}</Text>

        <Svg style={styles.svgTitle} width="800" height="40" viewBox="0 0 800 40">
          <Text style={styles.textmediumlarge} x="250" y="30">
            Pregled evidencije uređaja
          </Text>
        </Svg>

        <Text style={styles.summary}>Uređaja u izvještaju: {rows.length}</Text>

        <View style={styles.table}>
          <View style={{ ...styles.tableRow, ...styles.tableHeader }}>
            {REPORT_COLS.map((c) => (
              <View key={c.key} style={{ width: c.width }}>
                <Text style={c.left ? styles.tableCellLeft : styles.tableCellCenter}>{c.label}</Text>
              </View>
            ))}
          </View>

          {rows.map((device, index) => {
            const values = {
              rb: `${index + 1}.`,
              vlasnik: device.vlasnik,
              ime: device.ime,
              tiptrackera: device.tiptrackera,
              IMEI: device.IMEI,
              datumend: device.datumend,
            };

            return (
              <View
                key={device.id}
                wrap={false}
                style={index % 2 ? { ...styles.tableRow, ...styles.rowOdd } : styles.tableRow}
              >
                {REPORT_COLS.map((c) => (
                  <View key={c.key} style={{ width: c.width }}>
                    <Text style={c.left ? styles.tableCellLeft : styles.tableCellCenter}>
                      {String(values[c.key] ?? '')}
                    </Text>
                  </View>
                ))}
              </View>
            );
          })}
        </View>

        <View style={styles.officer}>
          <Text style={styles.officertext}>______________________________</Text>
          <Text style={styles.officertext}>( Knjigovođa )</Text>
        </View>

        <Text style={styles.mp}>M.P.</Text>

        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
}

// ----------------------------------------------------------------------

export default function DogsReportAll() {
  const { user: currentUser } = useSelector((state) => state.auth);

  const [devices, setDevices] = useState([]);
  const [deviceid, setDeviceId] = useState('');
  const [showdevicereport] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const getAllDevices = async () => {
    try {
      const response = await axios.get(`${SERVER_URL}/devices`);
      setDevices(response.data);
    } catch (err) {
      console.log('Greška pri učitavanju uređaja', err);
    }
  };

  // Učitaj jednom, pri otvaranju stranice
  useEffect(() => {
    if (!currentUser) return;
    getAllDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "vlasnik" kao običan tekst, da bi se po njemu moglo sortirati i pretraživati
  const devicesWithOwner = useMemo(
    () =>
      devices.map((d) => ({
        ...d,
        vlasnik: d.subscriber ? `${d.subscriber.ime} ${d.subscriber.prezime}` : '',
      })),
    [devices]
  );

  const filteredDevices = useMemo(
    () => applySortFilter(devicesWithOwner, getComparator(order, orderBy), filterName, orderBy),
    [devicesWithOwner, order, orderBy, filterName]
  );

  // Svi hookovi su iznad ove provjere
  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // --- Handleri ---

  const handleChange = (event, id) => {
    const device = devices.find((obj) => obj.id === id);
    if (!device) return;
    const novoStanje = device.aktivan ? 0 : 1;

    axios.put(`${SERVER_URL}/devices/${id}`, { aktivan: novoStanje }).then(() => {
      setDevices((prev) => prev.map((d) => (d.id === id ? { ...d, aktivan: novoStanje } : d)));
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleNo = () => {
    setOpen(false);
  };

  const handleYes = () => {
    axios.delete(`${SERVER_URL}/devices/${deviceid}`, {}).then(() => {
      getAllDevices();
    });
    setOpen(false);
  };

  // Za buduće Delete dugme u tabeli (trenutno ga nema u TABLE_HEAD)
  // eslint-disable-next-line no-unused-vars
  const handleClickDelete = (event, id) => {
    setDeviceId(id);
    setOpen(true);
  };

  const setData = (data) => {
    const {
      id,
      subscriberId,
      ime,
      tip,
      IMEI,
      tiptrackera,
      brojmobitela,
      bojaputanje,
      uploadinterval,
      needdisconnect,
      needreset,
      workmode,
      setuploadinterval,
      blokiran,
      datumstart,
      datumend,
      datumlast,
      brojlokacija,
    } = data;

    localStorage.setItem('id', id);
    localStorage.setItem('subscriberId', subscriberId);
    localStorage.setItem('ime', ime);
    localStorage.setItem('tip', tip);
    localStorage.setItem('IMEI', IMEI);
    localStorage.setItem('tiptrackera', tiptrackera);
    localStorage.setItem('brojmobitela', brojmobitela);
    localStorage.setItem('bojaputanje', bojaputanje);
    localStorage.setItem('uploadinterval', uploadinterval);
    localStorage.setItem('setuploadinterval', setuploadinterval);
    localStorage.setItem('needdisconnect', needdisconnect);
    localStorage.setItem('needreset', needreset);
    localStorage.setItem('workmode', workmode);
    localStorage.setItem('blokiran', blokiran);
    localStorage.setItem('datumstart', datumstart);
    localStorage.setItem('datumend', datumend);
    localStorage.setItem('datumlast', datumlast);
    localStorage.setItem('brojlokacija', brojlokacija);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');

    // Nova kolona = nova vrsta pretrage, pa stari tekst briše
    if (property !== orderBy) {
      setFilterName('');
      setPage(0);
    }
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredDevices.length) : 0;

  const isNotFound = !filteredDevices.length && !!filterName;

  const today = new Date();
  const datum = `Datum: ${today.getDate()}.${today.getMonth() + 1}.${today.getFullYear()}. god.`;

  const ukupnoKolona = TABLE_HEAD.length;

  // --- Render ---

  return (
    <>
      <Helmet>
        <title> GPS Dogs Tracking System </title>
      </Helmet>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Brisanje psa</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Da li ste sigurni?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNo}>Ne</Button>
          <Button onClick={handleYes} autoFocus>
            Da
          </Button>
        </DialogActions>
      </Dialog>

      <Container maxWidth={false}>
        <Typography variant="subtitle2" noWrap>
          Pregled evidencije uređaja
        </Typography>

        <Card>
          <UserListToolbar
            numSelected={0}
            filterName={filterName}
            onFilterName={handleFilterByName}
            placeholder={getSearchPlaceholder(orderBy)}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead order={order} orderBy={orderBy} headLabel={TABLE_HEAD} onRequestSort={handleRequestSort} />
                <TableBody>
                  {filteredDevices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const {
                      id,
                      ime,
                      tip,
                      IMEI,
                      tiptrackera,
                      aktivan,
                      online,
                      onlinetoday,
                      datumend,
                      saldo,
                      datumlast,
                      vlasnik,
                      subscriberId,
                      subscriber,
                    } = row;

                    const vlasnikId = subscriberId ?? subscriber?.id;

                    return (
                      <TableRow hover key={id} tabIndex={-1}>
                        <TableCell component="th" scope="row">
                          <Typography variant="subtitle2" noWrap>
                            {id}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar
                              alt={ime}
                              src={tip ? `/assets/images/lovackipsi/icon_${tip}.png` : undefined}
                              sx={{ width: 50, height: 50 }}
                              variant="rounded"
                            >
                              <Iconify icon="mdi:paw" width={28} />
                            </Avatar>
                            <Avatar
                              alt={tiptrackera}
                              src={
                                tiptrackera
                                  ? `/assets/images/trackers/icon_${tiptrackera.toLowerCase()}.png`
                                  : undefined
                              }
                              sx={{ width: 30, height: 30 }}
                              variant="rounded"
                            >
                              <Iconify icon="mdi:crosshairs-gps" width={18} />
                            </Avatar>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">
                          {vlasnik && vlasnikId ? (
                            <Typography
                              component={Link}
                              to={`${VLASNIK_URL}?vlasnik=${vlasnikId}`}
                              variant="subtitle2"
                              noWrap
                              sx={{
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {vlasnik}
                            </Typography>
                          ) : (
                            <Typography variant="subtitle2" noWrap>
                              {vlasnik}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell align="left">
                          <Typography variant="subtitle2" noWrap>
                            {ime}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Typography variant="subtitle2" noWrap>
                            {tiptrackera}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Typography variant="subtitle2" noWrap>
                            {IMEI}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Checkbox
                            color="success"
                            checked={Boolean(aktivan)}
                            onChange={(event) => handleChange(event, id)}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        </TableCell>

                        <TableCell>
                          <FormControlLabel control={<Android12Switch checked={Boolean(online)} />} label="" />
                        </TableCell>

                        <TableCell>
                          <FormControlLabel control={<Android12Switch checked={Boolean(onlinetoday)} />} label="" />
                        </TableCell>

                        <TableCell align="left">
                          <Typography variant="subtitle2" noWrap>
                            {datumlast}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Typography variant="subtitle2" noWrap>
                            {datumend}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Typography variant="subtitle2" noWrap>
                            {saldo}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Link to="/SLOBiHDogs/update">
                            <Button onClick={() => setData(row)}>Update</Button>
                          </Link>
                        </TableCell>

                        <TableCell>
                          <Link to="/SLOBiHDogs/sharedevice">
                            <Button onClick={() => setData(row)}>Share</Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={ukupnoKolona} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={ukupnoKolona} sx={{ py: 3 }}>
                        <Paper sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" paragraph>
                            Nema rezultata
                          </Typography>
                          <Typography variant="body2">
                            Ništa nije pronađeno za &nbsp;
                            <strong>&quot;{filterName}&quot;</strong> u koloni {getColumn(orderBy)?.label}.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredDevices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

        <br />

        <Button onClick={() => setLoading(!loading)}>
          {loading ? 'Zatvori izvještaj' : 'Generate Dogs Report'}
        </Button>

        <div>{showdevicereport && <DogReport dog={deviceid} />}</div>

        {loading && (
          <div className="child">
            <PDFViewer className="child">
              <DevicesReportAll rows={filteredDevices} datum={datum} />
            </PDFViewer>
          </div>
        )}
      </Container>
    </>
  );
}